// All site copy, in English and Arabic. Keep both objects in the same shape
// and keep factual claims identical in strength across both languages.
//
// Arabic terminology (keep consistent):
//   escrow → الضمان (Escrow) · transaction → المعاملة · agreed terms → الشروط المتفق عليها
//   completion → الإتمام · dispute procedure → إجراءات النزاع · documented steps → خطوات موثّقة

const en = {
  meta: {
    siteName: 'SafeHand',
    defaultTitle: 'SafeHand — Digital escrow & transaction management | Safe Hand Apps',
  },
  nav: {
    safehand: 'Platform',
    how: 'How it works',
    partners: 'Partnerships',
    studio: 'Technology',
    work: 'Our Work',
    about: 'About',
    faq: 'FAQ',
    contact: 'Contact',
    cta: 'Partner with us',
    langSwitch: 'العربية',
    menu: 'Menu',
  },
  common: {
    home: 'Home',
    downloadOn: 'Download on the',
    getItOn: 'Get it on',
    appStore: 'App Store',
    googlePlay: 'Google Play',
    learnMore: 'Learn more',
    talkToUs: 'Contact us',
    explore: 'Explore the platform',
    partner: 'Discuss a partnership',
    illustrative: 'Illustrative product screen',
  },
  status: {
    label: 'In development',
    text: 'SafeHand is not yet publicly available. Launch timing, pricing and payment-handling arrangements will be announced before public availability.',
  },
  hero: {
    eyebrow: 'Digital escrow & transaction management',
    title: 'Trust,',
    titleAccent: 'built into every transaction.',
    lead:
      'SafeHand is a platform in development that gives buyers and sellers — individuals and businesses — one shared framework for a transaction: agreed terms, documented steps, a visible status, and a defined path to completion or dispute.',
    primary: 'Explore the platform',
    secondary: 'Discuss a partnership',
    pillars: ['Agreed terms', 'Documented steps', 'Defined completion & dispute path'],
  },
  mock: {
    deal: 'Transaction',
    item: 'Used car · Toyota Corolla 2019',
    price: 'EGP 640,000',
    buyer: 'Buyer',
    seller: 'Seller',
    steps: ['Terms agreed', 'Payment stage', 'Delivery documented', 'Completion'],
    accepted: 'Terms accepted',
    shared: 'Same status visible to both parties',
    confirm: 'Confirm completion',
    chipA: 'Accepted by both parties',
    chipASmall: 'Terms · v2',
    chipB: 'Delivery photo added',
  },
  problem: {
    eyebrow: 'The problem',
    title: 'Valuable transactions still run on blind trust.',
    lead:
      'Every day, people and businesses in Egypt agree deals over social media, phone calls and chat apps — for cars, property deposits, devices, goods and services. One side usually has to take the first risk.',
    items: [
      {
        icon: 'fa-solid fa-user-slash',
        title: 'The buyer pays first…',
        text: '…and the seller stops responding, or delivers something different from what was agreed.',
      },
      {
        icon: 'fa-solid fa-box-open',
        title: 'The seller delivers first…',
        text: '…and payment is delayed, disputed, or tied to new conditions after delivery.',
      },
      {
        icon: 'fa-solid fa-comments',
        title: 'The terms live in a chat',
        text: 'When something goes wrong, there is no shared record of what was agreed, what happened, or what should happen next.',
      },
    ],
    answerTitle: 'SafeHand gives the transaction a structure.',
    answerText:
      'Terms are agreed before anyone acts, each step is documented, both sides see the same status, and a defined procedure applies if there is a disagreement.',
  },
  diff: {
    eyebrow: 'More than a payment',
    title: 'A payment moves money. A transaction needs structure.',
    lead: 'SafeHand is designed around the whole transaction, not only the transfer.',
    leftTitle: 'A typical transfer',
    left: [
      'Money moves first; the terms live in a chat',
      'No shared status for both sides',
      'Proof scattered across screenshots',
      'No defined process when parties disagree',
    ],
    rightTitle: 'A SafeHand transaction',
    right: [
      'Terms agreed and accepted by both parties first',
      'One status that both parties can follow',
      'Evidence attached to the transaction record',
      'A defined path to completion — or to a dispute',
    ],
  },
  how: {
    eyebrow: 'How it works',
    title: 'The intended transaction journey',
    lead: 'Four stages that both parties can follow from their phone.',
    steps: [
      {
        icon: 'fa-solid fa-file-signature',
        title: 'Agree on terms',
        text: 'One party sets out the item or service, the price, delivery terms and conditions. The other party reviews and accepts.',
      },
      {
        icon: 'fa-solid fa-building-columns',
        title: 'Payment stage',
        text: 'The buyer completes the payment step. Under the escrow model, funds are held by a neutral party until the agreed conditions are met.',
      },
      {
        icon: 'fa-solid fa-camera',
        title: 'Deliver & document',
        text: 'The seller delivers. Both parties update the status and attach supporting evidence such as photos, documents and notes.',
      },
      {
        icon: 'fa-solid fa-flag-checkered',
        title: 'Complete or raise a dispute',
        text: 'The buyer confirms completion. If something is not as agreed, a dispute is raised through the defined procedure instead of in chat messages.',
      },
    ],
    noteTitle: 'About the payment stage:',
    noteText:
      'how funds are received, held and released depends on the regulated operating model, which is still being finalized. See “How we approach money movement” below.',
  },
  features: {
    eyebrow: 'Platform capabilities',
    title: 'What the platform is designed to do',
    lead: 'The capabilities SafeHand is being built around. Availability will be confirmed at launch.',
    items: [
      { icon: 'fa-solid fa-file-contract', title: 'Structured agreements', text: 'Each transaction starts from terms that both parties review and accept.' },
      { icon: 'fa-solid fa-timeline', title: 'Shared transaction status', text: 'Both parties follow the same stage of the transaction, from agreement to completion.' },
      { icon: 'fa-solid fa-folder-open', title: 'Evidence & documentation', text: 'Photos, documents and notes are attached to the transaction, not scattered across chats.' },
      { icon: 'fa-solid fa-scale-balanced', title: 'Completion & dispute procedures', text: 'A clear completion step, and a defined procedure for raising and handling disagreements.' },
      { icon: 'fa-solid fa-clipboard-list', title: 'Activity history', text: 'Actions on a transaction are recorded, giving a clear history of what happened and when.' },
      { icon: 'fa-solid fa-rotate', title: 'Reliable processing', text: 'Requests are designed to be processed once, so a repeated tap or retry does not create a duplicate action.' },
    ],
  },
  audiences: {
    eyebrow: 'Who it is for',
    title: 'Built for individuals, businesses and the platforms that serve them',
    items: [
      { icon: 'fa-solid fa-car-side', title: 'Individuals', text: 'High-value personal transactions such as vehicles, property-related payments and valuable goods.', link: 'explore' },
      { icon: 'fa-solid fa-briefcase', title: 'Merchants & businesses', text: 'Commercial orders where delivery, quality and payment terms need to be clear to both sides.', link: 'partner' },
      { icon: 'fa-solid fa-store', title: 'Digital marketplaces', text: 'A structured buyer–seller transaction workflow, explored as an integration opportunity.', link: 'partner' },
      { icon: 'fa-solid fa-building-columns', title: 'Financial & technology partners', text: 'Banks, payment providers and technology companies interested in the regulated side of the model.', link: 'partner' },
    ],
  },
  buyersSellers: {
    buyers: {
      title: 'For buyers',
      points: [
        'Agree on exactly what you are buying before the payment stage',
        'Follow the status of the transaction at every step',
        'Raise a dispute through a defined procedure if something is not as agreed',
      ],
    },
    sellers: {
      title: 'For sellers',
      points: [
        'Start from terms the buyer has already accepted',
        'Document delivery with photos and notes on the record',
        'A clear completion step instead of chasing confirmation',
      ],
    },
  },
  model: {
    eyebrow: 'Operating model',
    title: 'How we approach money movement',
    lead:
      'Managing a transaction and holding money are different activities. SafeHand keeps them separate by design.',
    layer1Tag: 'What SafeHand builds',
    layer1Title: 'Transaction layer',
    layer1: ['Agreed terms', 'Transaction status', 'Evidence & records', 'Completion & dispute procedures'],
    layer2Tag: 'Licensed institutions',
    layer2Title: 'Financial layer',
    layer2: ['Receiving funds', 'Holding funds', 'Transfer & release', 'Refunds'],
    note:
      'SafeHand intends to offer payment handling only through appropriately licensed banks or payment providers, in line with applicable Egyptian regulation. No banking or payment partnership has been announced at this time.',
    ctaTitle: 'Banks, payment providers and marketplaces',
    ctaText: 'We welcome conversations about how SafeHand could work with your institution or platform.',
  },
  partnerBand: {
    title: 'Bring structure to the transactions you care about.',
    text: 'Whether you are a business, a marketplace, or a financial-services or technology partner, we would like to hear how SafeHand could fit your transactions.',
  },
  studio: {
    eyebrow: 'The technology foundation',
    title: 'Built in-house by Safe Hand Apps.',
    lead:
      'The team developing SafeHand designs and engineers its own software: the mobile app, backend services and admin tools. The same team has delivered apps and websites for clients in the medical, restaurant and real-estate sectors.',
    stats: [
      { value: '3', label: 'Sectors served: medical, restaurant, real estate' },
      { value: 'iOS + Android', label: 'Cross-platform apps with Flutter' },
      { value: 'Full stack', label: 'App, backend and admin dashboard' },
      { value: 'AR / EN', label: 'Arabic-first, bilingual products' },
    ],
    cta: 'Technology & services',
  },
  services: {
    pageTitle: 'Technology',
    eyebrow: 'Technology & product development',
    title: 'The engineering behind SafeHand.',
    lead:
      'We build our own platform end to end, and we take on selected development projects for businesses: mobile apps, backends, dashboards and websites.',
    items: [
      { icon: 'fa-solid fa-mobile-screen-button', title: 'Mobile apps', text: 'iOS and Android apps built with Flutter from a single codebase.' },
      { icon: 'fa-solid fa-server', title: 'Backends & APIs', text: 'Node.js / Express services with MongoDB, push notifications and third-party integrations.' },
      { icon: 'fa-solid fa-chart-line', title: 'Admin dashboards', text: 'Web dashboards to manage users, orders, content and reports.' },
      { icon: 'fa-solid fa-globe', title: 'Websites', text: 'Bilingual business websites and appointment-booking portals.' },
      { icon: 'fa-solid fa-pen-ruler', title: 'UI / UX design', text: 'Arabic-first interfaces designed around how your customers actually behave.' },
      { icon: 'fa-solid fa-rocket', title: 'Launch & support', text: 'App Store and Google Play publishing, updates and ongoing improvements.' },
    ],
    processTitle: 'How we work',
    process: [
      { title: 'Discover', text: 'We understand your business, users and goals, and define the scope together.' },
      { title: 'Design', text: 'Wireframes and screens you review and approve before development starts.' },
      { title: 'Build', text: 'Iterative development with test builds you can try on your own phone.' },
      { title: 'Launch & grow', text: 'We publish the app and continue with updates, fixes and new features.' },
    ],
    valuesTitle: 'What you can expect',
    values: [
      { icon: 'fa-solid fa-lightbulb', title: 'Initiative', text: 'We suggest improvements to your product, not only execute a list.' },
      { icon: 'fa-solid fa-award', title: 'Quality', text: 'Careful engineering and testing before every release.' },
      { icon: 'fa-solid fa-users', title: 'User focus', text: 'Every screen is designed around the people who will use it.' },
      { icon: 'fa-solid fa-handshake', title: 'Integrity', text: 'We respect our clients’ rights and the ownership of their ideas. Costs are agreed in advance.' },
    ],
    ctaTitle: 'Have a product to build?',
    ctaText: 'Tell us about it and we will reply with next steps.',
  },
  work: {
    pageTitle: 'Our Work',
    eyebrow: 'Portfolio',
    title: 'Software we have delivered',
    lead: 'Apps and websites we designed and built for clients in the medical, restaurant and real-estate sectors.',
    flagshipTag: 'Flagship product · in development',
    flagshipText:
      'Our own digital escrow and transaction management platform: agreed terms, documented steps, shared status and defined completion and dispute procedures.',
    featured: [
      { key: 'john', title: 'John Kids Clinic', tag: 'Medical', text: 'Clinic management app with appointment scheduling, patient records and doctor–patient chat.' },
      { key: 'clinic', title: 'Clinic', tag: 'Medical', text: 'Clinic app for scheduling, medical records and patient follow-up.' },
      { key: 'lamera', title: 'Lamera', tag: 'Restaurant', text: 'Restaurant & lounge app for ordering, table booking and delivery.' },
      { key: 'sedy', title: 'Sedy Abd Elrahman', tag: 'Restaurant', text: 'Ordering and delivery app for a local restaurant.' },
      { key: 'aqar', title: 'Aqar Elminya', tag: 'Real estate', text: 'Property listings to browse, buy or rent, with agent–client communication.' },
    ],
    sitesTitle: 'Client websites',
    sitesLead: 'Profile and appointment-booking websites built for our clients.',
    siteDesc: 'Profile & appointment booking',
    siteDescRealEstate: 'Real-estate listings website',
  },
  about: {
    pageTitle: 'About',
    eyebrow: 'About us',
    title: 'Building the infrastructure for trusted transactions.',
    lead:
      'Safe Hand Apps is a software company based in Beni Suef, Egypt. Our team has designed and built mobile apps and websites for clients in the medical, restaurant and real-estate sectors.',
    lead2:
      'Today our main focus is SafeHand, a digital escrow and transaction management platform we are developing in-house. Our development work continues alongside it, and the experience behind it is what we build SafeHand on.',
    missionTitle: 'Our mission',
    mission: 'Help people and businesses transact with more confidence through clear terms, documented steps and fair procedures.',
    visionTitle: 'Our vision',
    vision: 'Digital transaction infrastructure that individuals, businesses, marketplaces and financial institutions can rely on.',
    valuesTitle: 'Our values',
    values: 'Accuracy about what we have built. Responsible handling of money and data. Respect for our users, clients and their ideas.',
  },
  faq: {
    pageTitle: 'FAQ',
    eyebrow: 'FAQ',
    title: 'Frequently asked questions',
    groups: [
      {
        title: 'SafeHand platform',
        items: [
          {
            q: 'What is SafeHand?',
            a: 'SafeHand is a digital escrow and transaction management platform in development. It is designed to help two parties structure a transaction around agreed terms, documented steps, a shared status, and defined completion and dispute procedures.',
          },
          {
            q: 'Is SafeHand available now?',
            a: 'Not yet. SafeHand is in development and is not publicly available. Launch timing will be announced on this website. Businesses and partners can contact us in the meantime.',
          },
          {
            q: 'What is escrow?',
            a: 'Escrow is an arrangement in which a neutral party holds a payment until agreed conditions are met, and then releases it. SafeHand is building the transaction workflow around that model: terms, status, evidence, completion and disputes.',
          },
          {
            q: 'Does SafeHand hold my money?',
            a: 'The arrangements for receiving, holding and releasing funds, including the licensed institutions involved, will be published before public launch. SafeHand intends to handle payments only through appropriately licensed banks or payment providers. Nothing on this website is an offer of payment or custody services.',
          },
          {
            q: 'What happens if there is a disagreement?',
            a: 'The platform is designed with a defined dispute procedure, so a disagreement is raised and documented on the transaction record and handled according to published rules. Those rules will be available before launch. No platform can guarantee a particular outcome.',
          },
          {
            q: 'How much will it cost?',
            a: 'Pricing has not been announced yet. It will be published before public launch.',
          },
          {
            q: 'Can my business or platform work with SafeHand?',
            a: 'We welcome conversations with merchants, marketplaces, and financial-services and technology partners. Use the contact form and choose “Partnership”.',
          },
        ],
      },
      {
        title: 'Technology & development',
        items: [
          {
            q: 'What makes Safe Hand Apps different?',
            a: 'We respect our clients’ rights and the ownership of their ideas. We build the features you need, suggest improvements to the product, and stay in touch after launch to keep improving the app.',
          },
          {
            q: 'Are there any hidden charges?',
            a: 'No. Any additional cost is discussed and agreed with you in advance.',
          },
          {
            q: 'Which platforms do you build for?',
            a: 'We use Flutter to build for iOS and Android at the same time, which gives a consistent experience on both. We also build the backend, the admin dashboard and the website.',
          },
        ],
      },
    ],
    stillTitle: 'Still have a question?',
    stillText: 'Send us a message and our team will get back to you.',
  },
  contact: {
    pageTitle: 'Contact',
    eyebrow: 'Contact',
    title: 'Let’s talk.',
    lead: 'Partnership inquiries, questions about SafeHand, or a development project: send us a message and we will get back to you.',
    form: {
      name: 'Full name',
      email: 'Email',
      phone: 'Phone',
      company: 'Company / organization',
      topic: 'Topic',
      topics: [
        { key: 'partnership', label: 'Partnership: bank, payment provider or marketplace' },
        { key: 'business', label: 'Using SafeHand for my business' },
        { key: 'development', label: 'App or website development project' },
        { key: 'general', label: 'General question' },
      ],
      message: 'Message',
      send: 'Send message',
      sending: 'Sending…',
      success: 'Message sent. We will be in touch soon.',
      error: 'Something went wrong. Please try again, or email us directly.',
      required: 'Please fill in your name, email and message.',
    },
    info: {
      address: 'Address',
      phone: 'Phone',
      email: 'Email',
      whatsapp: 'WhatsApp',
      whatsappCta: 'Chat with us',
    },
  },
  footer: {
    blurb: 'SafeHand is a digital escrow and transaction management platform in development by Safe Hand Apps, a software company based in Beni Suef, Egypt.',
    product: 'Platform',
    company: 'Company',
    contact: 'Contact',
    rights: 'All rights reserved.',
  },
  notFound: {
    title: 'Page not found',
    text: 'The page you are looking for does not exist or has moved.',
    back: 'Back to home',
  },
};

const ar = {
  meta: {
    siteName: 'سيف هاند',
    defaultTitle: 'سيف هاند — منصة رقمية للضمان وإدارة المعاملات | Safe Hand Apps',
  },
  nav: {
    safehand: 'المنصة',
    how: 'طريقة العمل',
    partners: 'الشراكات',
    studio: 'التكنولوجيا',
    work: 'أعمالنا',
    about: 'من نحن',
    faq: 'الأسئلة الشائعة',
    contact: 'تواصل معنا',
    cta: 'كن شريكًا',
    langSwitch: 'English',
    menu: 'القائمة',
  },
  common: {
    home: 'الرئيسية',
    downloadOn: 'حمّله من',
    getItOn: 'متاح على',
    appStore: 'App Store',
    googlePlay: 'Google Play',
    learnMore: 'اعرف المزيد',
    talkToUs: 'تواصل معنا',
    explore: 'تعرّف على المنصة',
    partner: 'ناقش شراكة معنا',
    illustrative: 'شاشة توضيحية للمنتج',
  },
  status: {
    label: 'قيد التطوير',
    text: 'سيف هاند غير متاحة للجمهور حتى الآن. سيتم الإعلان عن موعد الإطلاق والأسعار وترتيبات التعامل مع المدفوعات قبل الإتاحة للجمهور.',
  },
  hero: {
    eyebrow: 'الضمان الرقمي (Escrow) وإدارة المعاملات',
    title: 'الثقة،',
    titleAccent: 'جزء من كل معاملة.',
    lead:
      'سيف هاند منصة قيد التطوير تمنح البائع والمشتري — أفرادًا وشركات — إطارًا واحدًا مشتركًا للمعاملة: شروط متفق عليها، وخطوات موثّقة، وحالة واضحة للطرفين، ومسار محدد للإتمام أو لإجراءات النزاع.',
    primary: 'تعرّف على المنصة',
    secondary: 'ناقش شراكة معنا',
    pillars: ['شروط متفق عليها', 'خطوات موثّقة', 'مسار محدد للإتمام والنزاع'],
  },
  mock: {
    deal: 'معاملة',
    item: 'سيارة مستعملة · تويوتا كورولا 2019',
    price: '640,000 ج.م',
    buyer: 'المشتري',
    seller: 'البائع',
    steps: ['الاتفاق على الشروط', 'مرحلة الدفع', 'توثيق التسليم', 'الإتمام'],
    accepted: 'تم قبول الشروط',
    shared: 'نفس الحالة ظاهرة للطرفين',
    confirm: 'تأكيد الإتمام',
    chipA: 'وافق عليها الطرفان',
    chipASmall: 'الشروط · نسخة 2',
    chipB: 'تمت إضافة صورة التسليم',
  },
  problem: {
    eyebrow: 'المشكلة',
    title: 'المعاملات المهمة ما زالت تعتمد على الثقة العمياء.',
    lead:
      'يتفق الناس والشركات في مصر يوميًا على صفقات عبر السوشيال ميديا والمكالمات وتطبيقات الدردشة — سيارات، ومقدمات عقارات، وأجهزة، وبضائع، وخدمات. وغالبًا يضطر أحد الطرفين إلى المخاطرة أولًا.',
    items: [
      {
        icon: 'fa-solid fa-user-slash',
        title: 'المشتري يدفع أولًا…',
        text: '…فيتوقف البائع عن الرد، أو يسلّم شيئًا مختلفًا عن المتفق عليه.',
      },
      {
        icon: 'fa-solid fa-box-open',
        title: 'البائع يسلّم أولًا…',
        text: '…فيتأخر الدفع، أو يُعترض عليه، أو تُضاف شروط جديدة بعد التسليم.',
      },
      {
        icon: 'fa-solid fa-comments',
        title: 'الشروط موجودة في محادثة',
        text: 'عند حدوث مشكلة لا يوجد سجل مشترك لما تم الاتفاق عليه، أو لما حدث، أو لما يجب أن يحدث بعد ذلك.',
      },
    ],
    answerTitle: 'سيف هاند تمنح المعاملة هيكلًا واضحًا.',
    answerText:
      'يُتفق على الشروط قبل أي خطوة، وتُوثَّق كل مرحلة، ويرى الطرفان نفس الحالة، وتُطبَّق إجراءات محددة في حالة الخلاف.',
  },
  diff: {
    eyebrow: 'أكثر من مجرد دفع',
    title: 'الدفع ينقل المال… أما المعاملة فتحتاج إلى هيكل.',
    lead: 'سيف هاند مصممة حول المعاملة بالكامل، وليس التحويل فقط.',
    leftTitle: 'تحويل مالي تقليدي',
    left: [
      'المال يتحرك أولًا، والشروط في محادثة',
      'لا توجد حالة مشتركة يتابعها الطرفان',
      'الإثباتات متفرقة في صور الشاشة',
      'لا توجد إجراءات محددة عند الخلاف',
    ],
    rightTitle: 'معاملة على سيف هاند',
    right: [
      'شروط يتفق عليها الطرفان ويقبلانها أولًا',
      'حالة واحدة يتابعها الطرفان',
      'إثباتات مرفقة بسجل المعاملة',
      'مسار محدد للإتمام — أو للنزاع',
    ],
  },
  how: {
    eyebrow: 'طريقة العمل',
    title: 'رحلة المعاملة كما صُممت',
    lead: 'أربع مراحل يتابعها الطرفان من الموبايل.',
    steps: [
      {
        icon: 'fa-solid fa-file-signature',
        title: 'الاتفاق على الشروط',
        text: 'يحدد أحد الطرفين المنتج أو الخدمة والسعر وشروط التسليم والضوابط، ثم يراجعها الطرف الآخر ويقبلها.',
      },
      {
        icon: 'fa-solid fa-building-columns',
        title: 'مرحلة الدفع',
        text: 'يُتم المشتري خطوة الدفع. وفي نموذج الضمان يحتفظ طرف محايد بالمبلغ حتى تتحقق الشروط المتفق عليها.',
      },
      {
        icon: 'fa-solid fa-camera',
        title: 'التسليم والتوثيق',
        text: 'يسلّم البائع، ويحدّث الطرفان حالة المعاملة ويرفقان ما يدعمها من صور ومستندات وملاحظات.',
      },
      {
        icon: 'fa-solid fa-flag-checkered',
        title: 'الإتمام أو فتح نزاع',
        text: 'يؤكد المشتري الإتمام. وإذا كان هناك ما يخالف الاتفاق يُفتح نزاع عبر الإجراءات المحددة بدلًا من رسائل الدردشة.',
      },
    ],
    noteTitle: 'بخصوص مرحلة الدفع:',
    noteText:
      'طريقة استلام المبالغ والاحتفاظ بها وتحويلها تعتمد على نموذج التشغيل المنظَّم الذي لا يزال قيد الإعداد. راجع قسم «منهجنا في التعامل مع حركة الأموال» بالأسفل.',
  },
  features: {
    eyebrow: 'قدرات المنصة',
    title: 'ما صُممت المنصة لتقدمه',
    lead: 'القدرات التي تُبنى حولها سيف هاند. سيتم تأكيد إتاحتها عند الإطلاق.',
    items: [
      { icon: 'fa-solid fa-file-contract', title: 'اتفاقات منظّمة', text: 'تبدأ كل معاملة بشروط يراجعها الطرفان ويقبلانها.' },
      { icon: 'fa-solid fa-timeline', title: 'حالة مشتركة للمعاملة', text: 'يتابع الطرفان نفس مرحلة المعاملة من الاتفاق حتى الإتمام.' },
      { icon: 'fa-solid fa-folder-open', title: 'الإثباتات والتوثيق', text: 'الصور والمستندات والملاحظات مرفقة بالمعاملة، لا متفرقة في المحادثات.' },
      { icon: 'fa-solid fa-scale-balanced', title: 'إجراءات الإتمام والنزاع', text: 'خطوة واضحة للإتمام، وإجراءات محددة لفتح الخلافات والتعامل معها.' },
      { icon: 'fa-solid fa-clipboard-list', title: 'سجل النشاط', text: 'تُسجَّل الإجراءات التي تتم على المعاملة، فيكون هناك تاريخ واضح لما حدث ومتى.' },
      { icon: 'fa-solid fa-rotate', title: 'معالجة موثوقة', text: 'صُممت الطلبات لتُعالَج مرة واحدة، فلا يؤدي تكرار الضغط أو إعادة المحاولة إلى إجراء مكرر.' },
    ],
  },
  audiences: {
    eyebrow: 'لمن صُممت',
    title: 'للأفراد والشركات والمنصات التي تخدمهم',
    items: [
      { icon: 'fa-solid fa-car-side', title: 'الأفراد', text: 'المعاملات الشخصية عالية القيمة مثل السيارات والمدفوعات المتعلقة بالعقارات والسلع الثمينة.', link: 'explore' },
      { icon: 'fa-solid fa-briefcase', title: 'التجار والشركات', text: 'الطلبيات التجارية التي تحتاج إلى وضوح شروط التسليم والجودة والدفع للطرفين.', link: 'partner' },
      { icon: 'fa-solid fa-store', title: 'المنصات والأسواق الرقمية', text: 'مسار منظّم للمعاملات بين البائع والمشتري، كفرصة تكامل قيد الاستكشاف.', link: 'partner' },
      { icon: 'fa-solid fa-building-columns', title: 'الشركاء الماليون والتقنيون', text: 'البنوك ومقدمو خدمات الدفع وشركات التكنولوجيا المهتمون بالجانب المنظَّم من النموذج.', link: 'partner' },
    ],
  },
  buyersSellers: {
    buyers: {
      title: 'للمشترين',
      points: [
        'اتفق على ما تشتريه بالضبط قبل مرحلة الدفع',
        'تابع حالة المعاملة في كل خطوة',
        'افتح نزاعًا عبر إجراءات محددة إذا كان هناك ما يخالف الاتفاق',
      ],
    },
    sellers: {
      title: 'للبائعين',
      points: [
        'ابدأ من شروط قبلها المشتري بالفعل',
        'وثّق التسليم بالصور والملاحظات في سجل المعاملة',
        'خطوة إتمام واضحة بدلًا من ملاحقة التأكيد',
      ],
    },
  },
  model: {
    eyebrow: 'نموذج التشغيل',
    title: 'منهجنا في التعامل مع حركة الأموال',
    lead: 'إدارة المعاملة والاحتفاظ بالأموال نشاطان مختلفان، وسيف هاند تفصل بينهما في تصميمها.',
    layer1Tag: 'ما تبنيه سيف هاند',
    layer1Title: 'طبقة المعاملة',
    layer1: ['الشروط المتفق عليها', 'حالة المعاملة', 'الإثباتات والسجلات', 'إجراءات الإتمام والنزاع'],
    layer2Tag: 'مؤسسات مرخّصة',
    layer2Title: 'الطبقة المالية',
    layer2: ['استلام الأموال', 'الاحتفاظ بالأموال', 'التحويل والصرف', 'الاسترداد'],
    note:
      'تعتزم سيف هاند تقديم خدمات التعامل مع المدفوعات فقط من خلال بنوك أو مقدمي خدمات دفع مرخّصين بشكل مناسب، ووفقًا للوائح المصرية المعمول بها. ولم يتم الإعلان عن أي شراكة مصرفية أو شراكة دفع حتى الآن.',
    ctaTitle: 'البنوك ومقدمو خدمات الدفع والمنصات الرقمية',
    ctaText: 'نرحّب بالحوار حول كيفية عمل سيف هاند مع مؤسستكم أو منصتكم.',
  },
  partnerBand: {
    title: 'أضف هيكلًا واضحًا للمعاملات التي تهمك.',
    text: 'سواء كنت شركة، أو منصة رقمية، أو شريكًا في الخدمات المالية أو التكنولوجيا، يسعدنا أن نعرف كيف يمكن أن تخدم سيف هاند معاملاتك.',
  },
  studio: {
    eyebrow: 'الأساس التقني',
    title: 'يطوّرها فريق Safe Hand Apps داخليًا.',
    lead:
      'الفريق الذي يطوّر سيف هاند يصمم ويبني برمجياته بنفسه: تطبيق الموبايل والخدمات الخلفية وأدوات الإدارة. وقد سلّم نفس الفريق تطبيقات ومواقع لعملاء في القطاعات الطبية والمطاعم والعقارات.',
    stats: [
      { value: '3', label: 'قطاعات: الطبي، المطاعم، العقارات' },
      { value: 'iOS + Android', label: 'تطبيقات متعددة المنصات بـ Flutter' },
      { value: 'Full stack', label: 'التطبيق والسيرفر ولوحة التحكم' },
      { value: 'AR / EN', label: 'منتجات ثنائية اللغة، العربية أولًا' },
    ],
    cta: 'التكنولوجيا والخدمات',
  },
  services: {
    pageTitle: 'التكنولوجيا',
    eyebrow: 'التكنولوجيا وتطوير المنتجات',
    title: 'الهندسة وراء سيف هاند.',
    lead: 'نبني منصتنا بالكامل بأنفسنا، وننفذ مشروعات تطوير مختارة للشركات: تطبيقات الموبايل، والسيرفرات، ولوحات التحكم، والمواقع.',
    items: [
      { icon: 'fa-solid fa-mobile-screen-button', title: 'تطبيقات الموبايل', text: 'تطبيقات iOS وAndroid باستخدام Flutter من كود واحد.' },
      { icon: 'fa-solid fa-server', title: 'السيرفرات والـ APIs', text: 'خدمات Node.js / Express مع MongoDB والإشعارات والتكامل مع الخدمات الخارجية.' },
      { icon: 'fa-solid fa-chart-line', title: 'لوحات التحكم', text: 'لوحات ويب لإدارة المستخدمين والطلبات والمحتوى والتقارير.' },
      { icon: 'fa-solid fa-globe', title: 'المواقع الإلكترونية', text: 'مواقع شركات ثنائية اللغة وبوابات لحجز المواعيد.' },
      { icon: 'fa-solid fa-pen-ruler', title: 'تصميم UI / UX', text: 'واجهات بالعربية أولًا، مصممة حول سلوك عملائك الفعلي.' },
      { icon: 'fa-solid fa-rocket', title: 'الإطلاق والدعم', text: 'النشر على App Store وGoogle Play والتحديثات والتطوير المستمر.' },
    ],
    processTitle: 'طريقة عملنا',
    process: [
      { title: 'الفهم', text: 'نفهم نشاطك ومستخدميك وأهدافك، ونحدد نطاق العمل معًا.' },
      { title: 'التصميم', text: 'تصميمات وشاشات تراجعها وتوافق عليها قبل بدء التطوير.' },
      { title: 'التطوير', text: 'تطوير على مراحل مع نسخ تجريبية تجربها على موبايلك.' },
      { title: 'الإطلاق والنمو', text: 'ننشر التطبيق ونستمر في التحديثات والإصلاحات والمميزات الجديدة.' },
    ],
    valuesTitle: 'ماذا تتوقع منا',
    values: [
      { icon: 'fa-solid fa-lightbulb', title: 'المبادرة', text: 'نقترح تحسينات لمنتجك، لا مجرد تنفيذ قائمة طلبات.' },
      { icon: 'fa-solid fa-award', title: 'الجودة', text: 'هندسة واختبار دقيقان قبل كل إصدار.' },
      { icon: 'fa-solid fa-users', title: 'التركيز على المستخدم', text: 'كل شاشة مصممة حول الأشخاص الذين سيستخدمونها.' },
      { icon: 'fa-solid fa-handshake', title: 'النزاهة', text: 'نحترم حقوق عملائنا وملكيتهم لأفكارهم، ويتم الاتفاق على التكاليف مسبقًا.' },
    ],
    ctaTitle: 'لديك منتج تريد بناءه؟',
    ctaText: 'احكِ لنا عنه وسنرد عليك بالخطوات التالية.',
  },
  work: {
    pageTitle: 'أعمالنا',
    eyebrow: 'أعمالنا',
    title: 'برمجيات قمنا بتسليمها',
    lead: 'تطبيقات ومواقع صممناها وبنيناها لعملاء في القطاعات الطبية والمطاعم والعقارات.',
    flagshipTag: 'منتجنا الرئيسي · قيد التطوير',
    flagshipText:
      'منصتنا الرقمية للضمان وإدارة المعاملات: شروط متفق عليها، وخطوات موثّقة، وحالة مشتركة، وإجراءات محددة للإتمام والنزاع.',
    featured: [
      { key: 'john', title: 'John Kids Clinic', tag: 'طبي', text: 'تطبيق لإدارة العيادة مع جدولة المواعيد وملفات المرضى والمحادثة بين الطبيب والمريض.' },
      { key: 'clinic', title: 'Clinic', tag: 'طبي', text: 'تطبيق عيادة لجدولة المواعيد والسجلات الطبية ومتابعة المرضى.' },
      { key: 'lamera', title: 'Lamera', tag: 'مطاعم', text: 'تطبيق مطعم ولاونج للطلب وحجز الطاولات والتوصيل.' },
      { key: 'sedy', title: 'سيدي عبد الرحمن', tag: 'مطاعم', text: 'تطبيق طلب وتوصيل لمطعم محلي.' },
      { key: 'aqar', title: 'عقار المنيا', tag: 'عقارات', text: 'تصفح العقارات للبيع أو الإيجار مع التواصل بين الوسيط والعميل.' },
    ],
    sitesTitle: 'مواقع العملاء',
    sitesLead: 'مواقع تعريفية وحجز مواعيد بنيناها لعملائنا.',
    siteDesc: 'موقع تعريفي وحجز مواعيد',
    siteDescRealEstate: 'موقع إعلانات عقارية',
  },
  about: {
    pageTitle: 'من نحن',
    eyebrow: 'من نحن',
    title: 'نبني البنية التحتية لمعاملات أكثر ثقة.',
    lead:
      'Safe Hand Apps شركة برمجيات مقرها بني سويف، مصر. صمم فريقنا وبنى تطبيقات ومواقع لعملاء في القطاعات الطبية والمطاعم والعقارات.',
    lead2:
      'واليوم ينصبّ تركيزنا الأساسي على سيف هاند، منصة رقمية للضمان وإدارة المعاملات نطوّرها داخليًا. ويستمر عملنا في التطوير إلى جانبها، وهو الخبرة التي نبني عليها سيف هاند.',
    missionTitle: 'رسالتنا',
    mission: 'مساعدة الأفراد والشركات على إتمام معاملاتهم بثقة أكبر، من خلال شروط واضحة وخطوات موثّقة وإجراءات عادلة.',
    visionTitle: 'رؤيتنا',
    vision: 'بنية تحتية رقمية للمعاملات يعتمد عليها الأفراد والشركات والمنصات الرقمية والمؤسسات المالية.',
    valuesTitle: 'قيمنا',
    values: 'الدقة في الحديث عمّا أنجزناه. المسؤولية في التعامل مع الأموال والبيانات. احترام مستخدمينا وعملائنا وأفكارهم.',
  },
  faq: {
    pageTitle: 'الأسئلة الشائعة',
    eyebrow: 'الأسئلة الشائعة',
    title: 'أسئلة متكررة',
    groups: [
      {
        title: 'منصة سيف هاند',
        items: [
          {
            q: 'ما هي سيف هاند؟',
            a: 'سيف هاند منصة رقمية للضمان (Escrow) وإدارة المعاملات، قيد التطوير. صُممت لمساعدة طرفي المعاملة على تنظيمها حول شروط متفق عليها، وخطوات موثّقة، وحالة مشتركة، وإجراءات محددة للإتمام والنزاع.',
          },
          {
            q: 'هل سيف هاند متاحة الآن؟',
            a: 'ليس بعد. سيف هاند قيد التطوير وغير متاحة للجمهور، وسيتم الإعلان عن موعد الإطلاق على هذا الموقع. ويمكن للشركات والشركاء التواصل معنا حتى ذلك الحين.',
          },
          {
            q: 'ما هو الضمان (Escrow)؟',
            a: 'الضمان ترتيب يحتفظ فيه طرف محايد بالمبلغ حتى تتحقق الشروط المتفق عليها، ثم يحوّله. وتبني سيف هاند مسار المعاملة حول هذا النموذج: الشروط، والحالة، والإثباتات، والإتمام، والنزاعات.',
          },
          {
            q: 'هل تحتفظ سيف هاند بأموالي؟',
            a: 'سيتم نشر ترتيبات استلام المبالغ والاحتفاظ بها وتحويلها — بما في ذلك المؤسسات المرخّصة المعنية — قبل الإطلاق للجمهور. وتعتزم سيف هاند التعامل مع المدفوعات فقط من خلال بنوك أو مقدمي خدمات دفع مرخّصين بشكل مناسب. ولا يُعد أي محتوى على هذا الموقع عرضًا لخدمات دفع أو حفظ أموال.',
          },
          {
            q: 'ماذا يحدث في حالة الخلاف؟',
            a: 'صُممت المنصة بإجراءات نزاع محددة، بحيث يُفتح الخلاف ويُوثَّق في سجل المعاملة ويُتعامل معه وفق قواعد منشورة، وستُتاح هذه القواعد قبل الإطلاق. ولا يمكن لأي منصة ضمان نتيجة بعينها.',
          },
          {
            q: 'كم ستكون التكلفة؟',
            a: 'لم يتم الإعلان عن الأسعار بعد، وسيتم نشرها قبل الإطلاق للجمهور.',
          },
          {
            q: 'هل يمكن لشركتي أو منصتي العمل مع سيف هاند؟',
            a: 'نرحّب بالحوار مع التجار والمنصات الرقمية والشركاء في الخدمات المالية والتكنولوجيا. استخدم نموذج التواصل واختر «شراكة».',
          },
        ],
      },
      {
        title: 'التكنولوجيا والتطوير',
        items: [
          {
            q: 'ما الذي يميز شركة Safe Hand Apps عن غيرها؟',
            a: 'نحترم حقوق عملائنا وملكيتهم لأفكارهم. نبني المميزات التي تحتاجها، ونقترح تحسينات للمنتج، ونظل على تواصل بعد الإطلاق لنواصل تحسين التطبيق.',
          },
          {
            q: 'هل توجد تكاليف خفية؟',
            a: 'لا. أي تكلفة إضافية تتم مناقشتها والاتفاق عليها معك مسبقًا.',
          },
          {
            q: 'لأي منصات تطورون التطبيقات؟',
            a: 'نستخدم Flutter للتطوير على iOS وAndroid في نفس الوقت، مما يمنح تجربة متسقة على المنصتين. ونبني أيضًا السيرفر ولوحة التحكم والموقع.',
          },
        ],
      },
    ],
    stillTitle: 'لديك سؤال آخر؟',
    stillText: 'أرسل لنا رسالة وسيرد عليك فريقنا.',
  },
  contact: {
    pageTitle: 'تواصل معنا',
    eyebrow: 'تواصل معنا',
    title: 'يسعدنا تواصلك.',
    lead: 'استفسارات الشراكة، أو أسئلة عن سيف هاند، أو مشروع تطوير: أرسل لنا رسالة وسنرد عليك.',
    form: {
      name: 'الاسم بالكامل',
      email: 'البريد الإلكتروني',
      phone: 'رقم الهاتف',
      company: 'الشركة / الجهة',
      topic: 'الموضوع',
      topics: [
        { key: 'partnership', label: 'شراكة: بنك أو مقدم خدمات دفع أو منصة رقمية' },
        { key: 'business', label: 'استخدام سيف هاند في نشاطي التجاري' },
        { key: 'development', label: 'مشروع تطوير تطبيق أو موقع' },
        { key: 'general', label: 'سؤال عام' },
      ],
      message: 'الرسالة',
      send: 'إرسال الرسالة',
      sending: 'جارٍ الإرسال…',
      success: 'تم إرسال رسالتك، وسنتواصل معك قريبًا.',
      error: 'حدث خطأ. حاول مرة أخرى، أو راسلنا عبر البريد الإلكتروني مباشرة.',
      required: 'من فضلك اكتب الاسم والبريد الإلكتروني والرسالة.',
    },
    info: {
      address: 'العنوان',
      phone: 'الهاتف',
      email: 'البريد الإلكتروني',
      whatsapp: 'واتساب',
      whatsappCta: 'تحدث معنا',
    },
  },
  footer: {
    blurb: 'سيف هاند منصة رقمية للضمان وإدارة المعاملات، قيد التطوير حاليًا لدى شركة Safe Hand Apps للبرمجيات، ومقرها بني سويف، مصر.',
    product: 'المنصة',
    company: 'الشركة',
    contact: 'التواصل',
    rights: 'جميع الحقوق محفوظة.',
  },
  notFound: {
    title: 'الصفحة غير موجودة',
    text: 'الصفحة التي تبحث عنها غير موجودة أو تم نقلها.',
    back: 'العودة للرئيسية',
  },
};

const content = { en, ar };
export default content;
